import { io } from 'socket.io-client';
import { SERVER_URL } from './api';
import { trackActivity } from './activity';

/**
 * One shared socket for the whole app.
 *
 * Created lazily on first use so a logged-out visitor never opens a connection,
 * and torn down on logout so the next user doesn't inherit the previous
 * session's identity.
 */

let socket = null;
let stopActivity = () => { };

/*
 * What this session is, for "Harshit (PC)" when someone is on a call from two
 * devices. A browser on a phone is the phone.
 */
const deviceKind = () => (
    /Mobi|Android|iPhone|iPad/.test(navigator.userAgent || '') ? 'phone' : 'pc'
);

// conversation id -> how many parts of the app want its room.
const roomHolds = new Map();

export const getSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    if (!socket) {
        socket = io(SERVER_URL || window.location.origin, {
            // `client` tells the server this tab can take calls through the
            // media server (server/utils/appVersion.js).
            // A function, so a reconnect sends the newest token (utils/session.js renews it).
            auth: (cb) => cb({ token: localStorage.getItem('token') || token, device: deviceKind(), client: 'web' }),
            transports: ['websocket', 'polling'],
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000
        });

        // A reconnect lands in a fresh server-side socket with no rooms.
        socket.on('connect', () => {
            for (const id of roomHolds.keys()) socket.emit('conversation:join', id);
        });

        socket.on('connect_error', (err) => {
            // The session has ended: sign out (utils/session.js) rather than retry for ever.
            if (err.message === 'SESSION_ENDED') {
                window.dispatchEvent(new CustomEvent('gts:session-ended', { detail: { reason: 'ended' } }));
                return;
            }
            // Expected while the server restarts; socket.io retries on its own.
            console.debug('[socket] connect error:', err.message);
        });

        // Signed out elsewhere, a password change, a deactivated account.
        socket.on('session:ended', (payload) => {
            window.dispatchEvent(new CustomEvent('gts:session-ended', { detail: { reason: payload?.reason || 'ended' } }));
        });

        // Idle or in the background: colleagues see "Away" (utils/activity.js).
        stopActivity = trackActivity(socket);
    }

    return socket;
};

export const disconnectSocket = () => {
    roomHolds.clear();
    stopActivity();
    stopActivity = () => { };
    if (socket) {
        socket.disconnect();
        socket = null;
    }
};

/**
 * Subscribe to an event and get an unsubscribe function back, so React effects
 * can clean up without leaking listeners across re-renders.
 */
export const onSocket = (event, handler) => {
    const s = getSocket();
    if (!s) return () => { };
    s.on(event, handler);
    return () => s.off(event, handler);
};

/**
 * Be in a conversation's room until the returned function is called.
 *
 * Counted, because rooms are not: the chat page and a call's chat panel can
 * have the same conversation open at once, and a plain `conversation:leave`
 * from whichever closes first would silently stop the other receiving
 * messages. The room is only left when the last holder lets go.
 */
export const holdConversationRoom = (conversationId) => {
    const s = getSocket();
    if (!s || !conversationId) return () => { };

    const id = String(conversationId);
    const count = roomHolds.get(id) || 0;
    roomHolds.set(id, count + 1);
    if (!count) s.emit('conversation:join', id);

    let released = false;
    return () => {
        if (released) return;
        released = true;
        const left = (roomHolds.get(id) || 1) - 1;
        if (left > 0) {
            roomHolds.set(id, left);
            return;
        }
        roomHolds.delete(id);
        getSocket()?.emit('conversation:leave', id);
    };
};

export default getSocket;
